import React, { useMemo, useState } from "react";
import PrimaryButton from "../../designSystem/buttons/PrimaryButton";
import SecondaryButton from "../../designSystem/buttons/SecondaryButton";
import { formatCurrency } from "./HelperFunctions/utils";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";
import CustomSelect from "../../designSystem/CustomSelect";
import FormInput from "../../designSystem/FormInput";

export default function MortgageRepaymentCalculator() {
  const [formData, setFormData] = useState({
    numberOfPersons: "1",
    mortgageType: "",
    propertyPrice: "",
    deposit: "",
    interestRate: "3.9",
    repaymentPeriod: "25",
  });

  const [hasCalculated, setHasCalculated] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setFormData({
      numberOfPersons: "1",
      mortgageType: "",
      propertyPrice: "",
      deposit: "",
      interestRate: "3.9",
      repaymentPeriod: "25",
    });
    setHasCalculated(false);
  };

  const handleCalculate = (e) => {
    e.preventDefault();
    setHasCalculated(true);
  };

  const formatPercent = (value) => {
    return Number(value || 0).toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const results = useMemo(() => {
    const propertyPrice = Number(formData.propertyPrice || 0);
    const deposit = Number(formData.deposit || 0);
    const interestRate = Number(formData.interestRate || 0);
    const repaymentPeriod = Number(formData.repaymentPeriod || 0);

    if (
      !propertyPrice ||
      !interestRate ||
      !repaymentPeriod ||
      !formData.mortgageType ||
      deposit < 0 ||
      deposit >= propertyPrice
    ) {
      return null;
    }

    const loanAmount = propertyPrice - deposit;
    const monthlyRate = interestRate / 100 / 12;
    const totalMonths = repaymentPeriod * 12;

    let monthlyPayment = 0;
    let totalInterest = 0;

    if (formData.mortgageType === "repayment") {
      if (monthlyRate === 0) {
        monthlyPayment = loanAmount / totalMonths;
      } else {
        monthlyPayment =
          (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
          (Math.pow(1 + monthlyRate, totalMonths) - 1);
      }

      totalInterest = monthlyPayment * totalMonths - loanAmount;
    }

    if (formData.mortgageType === "interestOnly") {
      monthlyPayment = loanAmount * monthlyRate;
      totalInterest = monthlyPayment * totalMonths;
    }

    const ltv = propertyPrice > 0 ? (loanAmount / propertyPrice) * 100 : 0;
    const depositPercent =
      propertyPrice > 0 ? (deposit / propertyPrice) * 100 : 0;

    return {
      loanAmount,
      monthlyPayment,
      totalInterest,
      ltv,
      depositPercent,
    };
  }, [formData]);

  return (
    <div className="background-container bg-gray-3">
      <div className="content-container py-xxl px-xxxl column gap-xl">
        <h4 className="italic uppercase primary">About You</h4>

        <CustomSelect
          title="Number of person(s)"
          name="numberOfPersons"
          options={[
            { value: "", label: "Please select" },
            { value: "1", label: "One person" },
            { value: "2", label: "Two people" },
          ]}
          value={formData.numberOfPersons}
          onChange={handleChange}
          placeholder="Please select number of persons"
        />

        <CustomSelect
          title="Mortgage type"
          name="mortgageType"
          options={[
            { value: "", label: "Please select" },
            { value: "repayment", label: "Repayment mortgage" },
            { value: "interestOnly", label: "Interest only mortgage" },
          ]}
          value={formData.mortgageType}
          onChange={handleChange}
          placeholder="Please select mortgage type"
        />

        <FormattedNumberInput
          title="Full property price (£)"
          name="propertyPrice"
          value={formData.propertyPrice}
          onChange={(raw) =>
            setFormData((prev) => ({
              ...prev,
              propertyPrice: raw,
            }))
          }
          className="body1 primary borrow-input"
        />

        <FormattedNumberInput
          title="Deposit (£)"
          name="deposit"
          value={formData.deposit}
          onChange={(raw) =>
            setFormData((prev) => ({
              ...prev,
              deposit: raw,
            }))
          }
          className="body1 primary borrow-input"
        />

        <FormInput
          title="Interest rate (%)"
          type="text"
          name="interestRate"
          value={formData.interestRate}
          onChange={handleChange}
          className="body1 primary borrow-input"
        />

        <FormInput
          title="Mortgage duration (Years)"
          type="text"
          name="repaymentPeriod"
          value={formData.repaymentPeriod}
          onChange={handleChange}
          className="body1 primary borrow-input"
        />

        <div className="calculator-actions">
          <SecondaryButton onClick={handleClear} text="Clear" />
          <PrimaryButton onClick={handleCalculate} text="Calculate" />
        </div>
      </div>

      {hasCalculated && results && (
        <div className="background-container bg-primary-1 ">
          <div className="content-container py-xxl px-xxxl column gap-l">
          <div className="content">Result</div>

          <div className="result-section">
            <div className="body1">Mortgage Payment per month</div>
            <div className="content">
              £{formatCurrency(results.monthlyPayment)}
            </div>
            <p className="content">
              On a{" "}
              <strong>
                {formData.mortgageType === "repayment"
                  ? "repayment mortgage"
                  : "interest only mortgage"}
              </strong>{" "}
              of <strong>£{formatCurrency(results.loanAmount)}</strong> at{" "}
              <strong>{formatPercent(formData.interestRate)}%</strong> over{" "}
              <strong>{formData.repaymentPeriod} Years</strong>.
            </p>
            <p className="content">
              Total interest paid will be approx{" "}
              <strong>£{formatCurrency(results.totalInterest)}</strong> over the
              mortgage term.
            </p>
          </div>

          <div className="result-section">
            <div className="body1">
              Illustrative LTV (loan to value) percentage
            </div>
            <div className="content">{formatPercent(results.ltv)}%</div>
            <p className="body1">
              Lenders will typically require a deposit of at least{" "}
              <strong>5%</strong> of the property price (95% loan to value).
            </p>
            <p className="body1">
              Your deposit of{" "}
              <strong>£{formatCurrency(formData.deposit)}</strong> represents{" "}
              <strong>{formatPercent(results.depositPercent)}%</strong> of the
              property price, or an LTV of{" "}
              <strong>{formatPercent(results.ltv)}%</strong>.
            </p>
          </div>

          <div className="result-divider" />

          <div className="result-note">
            <div className="body1">PLEASE NOTE:</div>
            <p className="body1">
              This mortgage repayment calculator is an interactive tool which
              should be used for your guidance only, and must not form part of
              your financial decision making process.
            </p>
            <p className="body1">
              Your home may be repossessed if you do not keep up repayments on a
              mortgage or any other debt secured on it.
            </p>
          </div>
        </div>
        </div>
      )}
    </div>
  );
}
